import { Signal } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { withStorageSync } from '@angular-architects/ngrx-toolkit';
import { withEntities } from '@ngrx/signals/entities';
import { StoreBatchWeighing } from './model/registrations/store-batch-weighing';
import { StoreAnimalWeighing } from './model/registrations/store-animal-weighing';
import { StoreRemoval } from './model/registrations/store-removal';
import { StorePen } from './model/master-data/store-pen';
import { StoreGroup } from './model/master-data/store-group';
import { StoreFeed } from './model/master-data/store-feed';
import { StoreSection } from './model/master-data/store-section';
import { StoreTrial } from './model/master-data/store-trial';
import { StoreBatch } from './model/master-data/store-batch';
import { animalWeighingViewModelsConst } from '../../viewmodels/computed/animal-weighing.viewmodels.const';
import { sectionOverviewViewModelConst } from '../../viewmodels/computed/section-overview.viewmodel.const';
import { batchWeighingViewModelConst } from '../../viewmodels/computed/batch-weighing.viewmodel.const';
import { updateAnimalWeighings } from './data-store-animal-weighing.const';
import { updateBatchWeighings } from './data-store-batch-weighing.const';
import { updateRemovals } from './data-store-removal.const';
import { DataStoreState } from './data-store-state.type';
import { trialOverviewRowViewModelsConst } from '../../viewmodels/computed/trial-overview-row.viewmodels.const';
import { AfpTestData } from '../../services/storage/datastore/testdata/afpdata';

export const initialState: DataStoreState = {
  batchWeighings: {} as Record<string, StoreBatchWeighing>,
  animalWeghings: {} as Record<string, StoreAnimalWeighing>,
  removals: {} as Record<string, StoreRemoval>,
  pens: {} as Record<string, StorePen>,
  groups: {} as Record<string, StoreGroup>,
  feeds: {} as Record<string, StoreFeed>,
  sections: {} as Record<string, StoreSection>,
  trials: {} as Record<string, StoreTrial>,
  batches: {} as Record<string, StoreBatch>,
};

export const testState: DataStoreState = {
  batchWeighings: AfpTestData.batchWeighings,
  animalWeghings: AfpTestData.animalWeighings,
  removals: {} as Record<string, StoreRemoval>,
  pens: AfpTestData.pens,
  groups: AfpTestData.groups,
  feeds: AfpTestData.feeds,
  sections: AfpTestData.sections,
  trials: AfpTestData.trials,
  batches: AfpTestData.batches,
};

export const DataStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  // withState(testState),
  withEntities(),
  withStorageSync('datastore-0002'),
  withComputed(({ animalWeghings, pens, groups, feeds }) => ({
    animalWeighingsViewModel: animalWeighingViewModelsConst(animalWeghings, pens, groups, feeds),
  })),
  withComputed(({ sections, trials, batches, batchWeighings, animalWeghings }) => ({
    sectionOverviewViewModelFromStore: sectionOverviewViewModelConst(sections, trials, batches, batchWeighings, animalWeghings),
  })),
  withComputed(({ batchWeighings, batches }) => ({
    batchWeighingsViewModel: batchWeighingViewModelConst(batchWeighings, batches),
  })),
  withComputed(({ batchWeighings, sections, trials, pens, batches }) => ({
    trialOverviewViewModel: trialOverviewRowViewModelsConst(batchWeighings, sections, trials, pens, batches),
  })),
  withMethods((store) => ({
    hasData(): boolean {
      return [
        store.batchWeighings(),
        store.animalWeghings(),
        store.removals(),
        store.pens(),
        store.groups(),
        store.feeds(),
        store.sections(),
        store.trials(),
        store.batches(),
      ].some((records) => Object.keys(records).length > 0);
    },
    replaceAll(data: DataStoreState): void {
      patchState(store, data);
    },
    storeUpdateAllAnimalWeighings(
      newWeighings: Partial<StoreAnimalWeighing>[],
      originalWeighings: Signal<Record<string, StoreAnimalWeighing>>
    ): void {
      patchState(store, updateAnimalWeighings(newWeighings, originalWeighings));
    },
    storeUpdateAllBatchWeighings(
      newWeighings: Partial<StoreBatchWeighing>[],
      originalWeighings: Signal<Record<string, StoreBatchWeighing>>
    ): void {
      patchState(store, updateBatchWeighings(newWeighings, originalWeighings));
    },
    storeUpdateAllRemovals(
      newRemovals: Partial<StoreRemoval>[],
      originalRemovals: Signal<Record<string, StoreRemoval>>
    ): void {
      patchState(store, updateRemovals(newRemovals, originalRemovals));
    },
    clear(): void {
      patchState(store, {
        batchWeighings: {},
        animalWeghings: {},
        removals: {},
        pens: {},
        groups: {},
        feeds: {},
        sections: {},
        trials: {},
        batches: {},
      });
    }
  }))
);
